import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Typography } from 'antd';
import { GameRoom } from './types';
import { fitFloorScale, floorHeight, floorWidth, roomState, Seat, seatsForRoom, tablePosition } from './room-layout';

const seatPositions = [[0, -102], [-115, 59], [115, 59]];

export function SeatNode({ seat, index }: { seat: Seat; index: number }) {
  const [x, y] = seatPositions[index];
  const name = seat.kind === 'player' ? seat.player?.username || `Player ${seat.player?.user_id}` : seat.kind === 'bot' ? 'Bot' : 'Empty seat';
  const special = seat.player?.status === 'reserved' || seat.player?.status === 'disconnect';
  return (
    <g transform={`translate(${x} ${y})`} className={`floor-seat floor-seat--${seat.kind}${special ? ' floor-seat--pending' : ''}`}>
      <title>{name}{seat.player ? ` · ${seat.player.status}` : ''}</title>
      <rect className="seat-back" x="-28" y="-30" width="56" height="50" rx="18" />
      <circle className="seat-cushion" r="22" />
      {seat.kind === 'bot' ? <g className="seat-glyph"><rect x="-11" y="-8" width="22" height="16" rx="5" /><path d="M0 -13v5M-5 -1h1m8 0h1M-4 5h8" /></g> :
        seat.kind === 'player' ? <text className="seat-initial" textAnchor="middle" dy="5">{name.slice(0, 2).toUpperCase()}</text> :
          <path className="seat-empty-mark" d="M-5 0h10M0 -5v10" />}
      <text className="seat-name" textAnchor="middle" y={index === 0 ? -42 : 43}>{name.length > 15 ? `${name.slice(0, 13)}…` : name}</text>
      {special && <text className="seat-status" textAnchor="middle" y={index === 0 ? -58 : 58}>{seat.player?.status === 'reserved' ? 'RESERVED' : 'DISCONNECTED'}</text>}
    </g>
  );
}

export function TableNode({ room, index, count, selected, onSelect }: { room: GameRoom; index: number; count: number; selected: boolean; onSelect: () => void }) {
  const { x, y } = tablePosition(index, count);
  const state = roomState(room);
  const label = `Table ${room.room_id}, ${room.name}, ${state}, ${room.players.length} players, ${room.bot_count} bots`;
  return (
    <g transform={`translate(${x} ${y})`}>
      <g className={`floor-table floor-table--${state.toLowerCase()}${selected ? ' is-selected' : ''}`} role="button" tabIndex={0} aria-label={label}
        aria-pressed={selected} onClick={onSelect} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect(); } }}>
        <title>{room.external_room_id} · {room.name}</title>
        <ellipse className="table-shadow" cy="10" rx="101" ry="77" />
        <ellipse className="table-rim" rx="98" ry="73" />
        <ellipse className="table-felt" rx="87" ry="62" />
        <ellipse className="table-inlay" rx="78" ry="53" />
        <text className="table-number" textAnchor="middle" y="-13">TABLE {room.room_id}</text>
        <text className="table-state" textAnchor="middle" y="9">{state}</text>
        <text className="table-bet" textAnchor="middle" y="30">{room.bet.toLocaleString()} GOLD</text>
        {state === 'Playing' && <g className="table-cards"><rect x="-12" y="-48" width="15" height="20" rx="2" transform="rotate(-12)" /><rect x="0" y="-48" width="15" height="20" rx="2" transform="rotate(12)" /></g>}
        {seatsForRoom(room).map((seat, seatIndex) => <SeatNode key={seatIndex} seat={seat} index={seatIndex} />)}
        {room.players.length + room.bot_count > 3 && <text className="seat-status" textAnchor="middle" y="128">Seat count exceeds capacity</text>}
      </g>
    </g>
  );
}

export function RoomBackground({ height, width }: { height: number; width: number }) {
  return <g aria-hidden="true">
    <defs>
      <pattern id="floor-pattern" width="36" height="36" patternUnits="userSpaceOnUse"><path d="M36 0H0V36" fill="none" stroke="var(--floor-border)" strokeWidth=".5" /></pattern>
    </defs>
    <rect width={width} height={height} fill="var(--floor-surface)" rx="12" />
    <rect x="25" y="25" width={width - 50} height={height - 50} fill="url(#floor-pattern)" stroke="var(--floor-border)" strokeWidth="3" rx="8" />
    <rect x="37" y="37" width={width - 74} height={height - 74} fill="none" stroke="var(--floor-border)" strokeOpacity=".18" rx="3" />
    <path d={`M${width / 2 - 52} ${height - 25}h104`} stroke="var(--floor-surface)" strokeWidth="9" />
    <text x={width / 2} y={height - 18} textAnchor="middle" className="floor-entrance">ENTRANCE</text>
    {[65, width - 65].map((x) => <g key={x} transform={`translate(${x},65)`}><circle r="17" fill="var(--floor-success-bg)" stroke="var(--floor-success)" /><path d="M-10 0Q0 -26 4 -3Q24 -12 6 5Q5 25 -3 8Q-25 12 -10 0" fill="var(--floor-success)" /></g>)}
  </g>;
}

export function RoomView({ rooms, selectedID, onSelect }: { rooms: GameRoom[]; selectedID?: number; onSelect: (room: GameRoom) => void }) {
  const [zoom, setZoom] = useState<number | null>(null);
  const viewport = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 1460, height: 1050 });
  const height = floorHeight(rooms.length);
  const width = floorWidth(rooms.length);
  const scale = zoom ?? fitFloorScale(size.width, size.height, rooms.length);
  useEffect(() => {
    const element = viewport.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    viewport.current?.scrollTo?.({ top: 0, left: 0 });
  }, [zoom, rooms.length]);

  return <section className="room-view" aria-label="Game room floor plan">
    <div className="room-view-bar"><Typography.Text type="secondary">Floor plan · {rooms.length} tables on this page</Typography.Text>
      <Space.Compact aria-label="Floor controls">
        <Button size="small" aria-label="Zoom out" disabled={scale <= 0.25} onClick={() => setZoom(Math.max(.25, scale - .25))}>−</Button>
        <Button size="small" aria-label="Reset zoom" onClick={() => setZoom(1)}>{Math.round(scale * 100)}%</Button>
        <Button size="small" aria-label="Zoom in" disabled={scale >= 1.75} onClick={() => setZoom(Math.min(1.75, scale + .25))}>+</Button>
        <Button size="small" aria-label="Fit room" type={zoom === null ? 'primary' : 'default'} onClick={() => setZoom(null)}>Vừa khung</Button>
      </Space.Compact>
    </div>
    <div className="floor-scroll" ref={viewport}>
      <svg className="floor-canvas" viewBox={`0 0 ${width} ${height}`} style={{ width: width * scale, height: height * scale }} aria-label="Tables and three seats per table">
        <RoomBackground height={height} width={width} />
        {rooms.map((room, index) => <TableNode key={room.room_id} room={room} index={index} count={rooms.length} selected={selectedID === room.room_id} onSelect={() => onSelect(room)} />)}
        {!rooms.length && <g><text x={width / 2} y="175" className="floor-empty-title" textAnchor="middle">The floor is quiet.</text><text x={width / 2} y="210" className="floor-empty-copy" textAnchor="middle">No open tables assigned to this server.</text></g>}
      </svg>
    </div>
    <div className="floor-legend"><span><i className="legend-human" />Player</span><span><i className="legend-bot" />Bot</span><span><i className="legend-empty" />Empty</span><span><i className="legend-reserved" />Reserved / disconnected</span></div>
  </section>;
}
